import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SyncResult {
  processed: number;
  synced: number;
  errors: number;
  details: Array<{
    expert: string;
    status: string;
    message: string;
  }>;
}

export function CalendarSyncManager() {
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncResults, setSyncResults] = useState<SyncResult | null>(null);

  const triggerSync = async () => {
    setLoading(true);
    try {
      console.log('Triggering calendar sync...');
      
      const { data, error } = await supabase.functions.invoke('sync-calendar-bookings', {
        body: { manual: true }
      });

      if (error) {
        throw error;
      }

      console.log('Sync response:', data);
      
      if (data?.success) {
        setSyncResults(data.results);
        setLastSync(new Date());
        toast.success('Calendar sync completed successfully');
      } else {
        throw new Error(data?.error || 'Unknown sync error');
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error(`Sync failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Google Calendar Sync
              </CardTitle>
              <CardDescription>
                Sync consultation bookings from Google Calendar into the system
              </CardDescription>
            </div>
            <Button onClick={triggerSync} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {loading ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {lastSync && (
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Last sync: {lastSync.toLocaleString()}
              </AlertDescription>
            </Alert>
          )}

          <div className="text-sm text-muted-foreground">
            <p>This sync will:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Fetch events from each expert's Google Calendar</li>
              <li>Create new consultation bookings from calendar events</li>
              <li>Update existing bookings if calendar events changed</li>
              <li>Sync events from the last 30 days to next 90 days</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {syncResults && (
        <Card>
          <CardHeader>
            <CardTitle>Last Sync Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{syncResults.processed}</div>
                <div className="text-sm text-muted-foreground">Experts Processed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{syncResults.synced}</div>
                <div className="text-sm text-muted-foreground">Bookings Synced</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{syncResults.errors}</div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </div>
            </div>

            {syncResults.details && syncResults.details.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold">Expert Details:</h4>
                {syncResults.details.map((detail, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      {detail.status === 'success' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                      <span className="font-medium">{detail.expert}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={detail.status === 'success' ? 'default' : 'destructive'}>
                        {detail.status}
                      </Badge>
                      <span className="text-sm text-muted-foreground">{detail.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Automatic Sync Schedule</CardTitle>
          <CardDescription>
            Calendar sync runs automatically every 2 hours to keep bookings up to date
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            <p>The automatic sync process:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Runs every 2 hours during business hours</li>
              <li>Only syncs active experts with valid calendar links</li>
              <li>Preserves manually created bookings</li>
              <li>Updates booking status when calendar events are cancelled</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}